/*
 * Operator-console theme for the internal admin agent surface.
 *
 * Visual language transplanted from Henry's Wiki app: a flat,
 * terminal-inspired working tool. Monospace chrome labels, sharp 2px
 * corners on interactive elements (pills only for chips), hairline
 * 1px borders with no card shadows, 2px left-border stripes for
 * nested/secondary content, and snappy 100ms ease-out hover motion.
 *
 * Palette: near-white canvas, warm-off-white shell, monochrome ink,
 * with the Phoebe brand purple (#5533ee) carrying the page identity —
 * CTA, focus, active states, selection, and agent-output framing.
 *
 * Tokens are scoped to `.admin-agent-page` so they cannot bleed into
 * the surrounding app shell. Every rule inside the page consumes these
 * variables — never hard-coded colors.
 */

.admin-agent-page {
    /* Canvas */
    --aa-canvas: #ffffff;
    --aa-surface: #f7f7f5;
    --aa-surface-sunk: #f1f0ec;
    --aa-surface-hover: #eeede9;

    /* Ink */
    --aa-ink: #0a0a0a;
    --aa-ink-soft: #333333;
    --aa-ink-muted: #6b6b6b;
    --aa-ink-faint: #9a9a9a;
    --aa-ink-mono: #4b4b4b;

    /* Borders */
    --aa-border: #e8e6e1;
    --aa-border-strong: #d6d3ca;
    --aa-border-hairline: rgba(10, 10, 10, 0.06);

    /*
     * Accent — the Phoebe brand purple (#5533ee, same as the logo and
     * the app's training token). It carries the page's identity: primary
     * CTA, focus, active states, selection, and agent-output framing.
     */
    --aa-accent: #5533ee;
    --aa-accent-ink: #ffffff;
    --aa-accent-hover: #4629c7;
    --aa-purple: #5533ee;
    --aa-purple-strong: #4629c7;
    --aa-purple-soft: #eeeafd;
    --aa-purple-wash: #f7f5fe;
    --aa-purple-border: #d8cffb;
    --aa-focus: #5533ee;

    /* Status */
    --aa-success: #1f7a3a;
    --aa-success-soft: #e6f4ea;
    --aa-success-border: #b7dfc2;
    --aa-danger: #c0392b;
    --aa-danger-soft: #fbe9e6;
    --aa-danger-border: #e9c3bd;
    --aa-warning: #a06a0a;
    --aa-warning-soft: #fbf3df;
    --aa-warning-border: #ecdfba;
    --aa-info: #2f5aa8;
    --aa-info-soft: #e8eefa;
    --aa-info-border: #c4d3ee;

    /* Type stacks */
    --aa-font-sans:
        'Inter', 'Söhne', -apple-system, 'Segoe UI', 'Helvetica Neue',
        system-ui, sans-serif;
    --aa-font-mono:
        'JetBrains Mono', 'IBM Plex Mono', 'SFMono-Regular', 'Menlo', Consolas,
        monospace;

    /*
     * Rhythm — Wiki-style sharp scale. 2px on interactive chrome,
     * 4px on inputs/menus, 8px only on floating surfaces.
     */
    --aa-radius-sm: 2px;
    --aa-radius-md: 4px;
    --aa-radius-lg: 8px;
    --aa-shadow-soft: none;
    --aa-shadow-lift:
        0 1px 0 rgba(10, 10, 10, 0.03), 0 8px 24px -12px rgba(10, 10, 10, 0.08);

    background: var(--aa-surface);
    color: var(--aa-ink);
    font-family: var(--aa-font-sans);
    font-feature-settings:
        'cv02' on,
        'cv11' on,
        'ss01' on;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;

    /*
     * shadcn semantic-token overrides — reach every shared component
     * (Button, Card, Input, Tabs) that renders inside the admin agent
     * page so their neutral chrome adopts the console palette without
     * touching the shared component library. Portaled dialogs teleport
     * to <body> and stay on the default app tokens.
     */
    --background: #ffffff;
    --foreground: #0a0a0a;
    --card: #ffffff;
    --card-foreground: #0a0a0a;
    --popover: #ffffff;
    --popover-foreground: #0a0a0a;
    --primary: #5533ee;
    --primary-foreground: #ffffff;
    --secondary: #f7f7f5;
    --secondary-foreground: #0a0a0a;
    --muted: #f1f0ec;
    --muted-foreground: #6b6b6b;
    --accent: #f1f0ec;
    --accent-foreground: #0a0a0a;
    --border: #e8e6e1;
    --input: #e8e6e1;
    --ring: #5533ee;
    --destructive: #c0392b;
    /* shadcn radius token — flows into Button/Input/Tabs rounding. */
    --radius: 4px;
}

/*
 * Text-selection highlight — the old page used a purple/lavender wash
 * that Henry wants kept across rethemes.
 */
.admin-agent-page ::selection {
    background: #e3dcfc;
    color: var(--aa-ink);
}

/*
 * Composer polish — reach into the shared ChatInput surface and kill
 * dead vertical space between the input area and the leading-control
 * row. The composer keeps a hairline frame so it reads as the one
 * "terminal prompt" on the page.
 */
.admin-agent-page [data-testid='chat-input-composer'] {
    padding: 8px 10px;
    border-radius: var(--aa-radius-md);
    background: var(--aa-canvas);
    border: 1px solid var(--aa-border);
    box-shadow: none !important;
}
.admin-agent-page [data-testid='chat-input-composer']:focus-within {
    border-color: var(--aa-border-strong);
    box-shadow: none !important;
}
.admin-agent-page [data-testid='chat-input-composer'] [role='textbox'] {
    min-height: 22px !important;
    font-size: 14px;
    line-height: 1.45;
    border: 0 !important;
    outline: none !important;
    box-shadow: none !important;
}
.admin-agent-page [data-testid='chat-input-composer'] [role='textbox']:focus,
.admin-agent-page
    [data-testid='chat-input-composer']
    [role='textbox']:focus-visible {
    outline: none !important;
    box-shadow: none !important;
}
.admin-agent-page [data-testid='chat-input-composer'] [role='textbox'] + div {
    /* Placeholder overlay tracks the same 14px baseline. */
    font-size: 14px;
    line-height: 1.45;
}
.admin-agent-page [data-testid='chat-input-composer'] > div:last-child {
    margin-top: 6px;
}

/* Type utilities — mono chrome, quiet sans body. */
.admin-agent-page .aa-mono {
    font-family: var(--aa-font-mono);
    font-feature-settings: 'ss01' on;
    letter-spacing: -0.005em;
    color: var(--aa-ink-mono);
}
.admin-agent-page .aa-tnum {
    font-variant-numeric: tabular-nums;
}
.admin-agent-page .aa-title {
    font-family: var(--aa-font-mono);
    font-weight: 600;
    font-size: 13px;
    line-height: 1.4;
    letter-spacing: -0.005em;
    color: var(--aa-ink);
}
.admin-agent-page .aa-body {
    font-size: 14px;
    line-height: 1.5;
    color: var(--aa-ink);
}
.admin-agent-page .aa-meta {
    font-size: 13px;
    line-height: 1.45;
    color: var(--aa-ink-muted);
}
.admin-agent-page .aa-caption {
    font-size: 12px;
    line-height: 1.4;
    color: var(--aa-ink-faint);
    letter-spacing: 0;
}
.admin-agent-page .aa-hero {
    font-family: var(--aa-font-sans);
    font-weight: 500;
    font-size: 26px;
    line-height: 1.2;
    letter-spacing: -0.02em;
    color: var(--aa-ink);
}

/*
 * Uppercase micro-label — the Wiki app's signature section marker.
 * Used for panel section headings, landing eyebrows, dialog headers.
 */
.admin-agent-page .aa-eyebrow,
.admin-agent-page .aa-label {
    font-family: var(--aa-font-mono);
    font-size: 11px;
    line-height: 1.4;
    color: var(--aa-ink-muted);
    letter-spacing: 0.05em;
    text-transform: uppercase;
    font-weight: 500;
}

/* Ink tones */
.admin-agent-page .aa-ink {
    color: var(--aa-ink);
}
.admin-agent-page .aa-ink-soft {
    color: var(--aa-ink-soft);
}
.admin-agent-page .aa-ink-muted {
    color: var(--aa-ink-muted);
}
.admin-agent-page .aa-ink-faint {
    color: var(--aa-ink-faint);
}

/* Surfaces */
.admin-agent-page .aa-canvas {
    background: var(--aa-canvas);
}
.admin-agent-page .aa-surface {
    background: var(--aa-surface);
}
.admin-agent-page .aa-surface-sunk {
    background: var(--aa-surface-sunk);
}

/*
 * Cards — flat hairline boxes, sharp corners, no shadows. Shadows are
 * reserved for floating surfaces (menus, dialogs).
 */
.admin-agent-page .aa-card {
    background: var(--aa-canvas);
    border: 1px solid var(--aa-border);
    border-radius: var(--aa-radius-sm);
    box-shadow: none;
}
.admin-agent-page .aa-card-lift {
    background: var(--aa-canvas);
    border: 1px solid var(--aa-border);
    border-radius: var(--aa-radius-lg);
    box-shadow: var(--aa-shadow-lift);
}

/*
 * Left-border stripe — nested/secondary content hangs off a 2px rule
 * instead of sitting in a box (Wiki session-assistant treatment).
 */
.admin-agent-page .aa-stripe {
    border-left: 2px solid var(--aa-border);
    padding-left: 12px;
}
.admin-agent-page .aa-stripe-dashed {
    border-left: 2px dashed var(--aa-border);
    padding-left: 12px;
}

/* Flat interactive row — hover gets a soft wash, nothing else moves. */
.admin-agent-page .aa-row {
    border-radius: var(--aa-radius-sm);
    transition-property: background-color, color, border-color;
    transition-duration: 100ms;
    transition-timing-function: ease-out;
}
.admin-agent-page .aa-row:hover {
    background: var(--aa-surface-sunk);
}

/* Hairline utilities */
.admin-agent-page .aa-rule {
    border-color: var(--aa-border);
}
.admin-agent-page .aa-rule-hairline {
    border-color: var(--aa-border-hairline);
}
.admin-agent-page .aa-divider {
    border-top: 1px solid var(--aa-border);
}

/* Chips + pills — mono, hairline, pill-shaped. Dashed = faint variant. */
.admin-agent-page .aa-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 1px 8px;
    border: 1px solid var(--aa-border);
    border-radius: 999px;
    font-family: var(--aa-font-mono);
    font-size: 11.5px;
    line-height: 1.5;
    color: var(--aa-ink-muted);
    background: transparent;
    white-space: nowrap;
    transition-property: border-color, color;
    transition-duration: 100ms;
    transition-timing-function: ease-out;
}
.admin-agent-page .aa-chip-faint {
    border-style: dashed;
    color: var(--aa-ink-faint);
}
.admin-agent-page .aa-chip-success {
    background: var(--aa-success-soft);
    border-color: var(--aa-success-border);
    color: var(--aa-success);
}
.admin-agent-page .aa-chip-danger {
    background: var(--aa-danger-soft);
    border-color: var(--aa-danger-border);
    color: var(--aa-danger);
}
.admin-agent-page .aa-chip-warning {
    background: var(--aa-warning-soft);
    border-color: var(--aa-warning-border);
    color: var(--aa-warning);
}
.admin-agent-page .aa-chip-info {
    background: var(--aa-info-soft);
    border-color: var(--aa-info-border);
    color: var(--aa-info);
}

/* Status dots — bare tone, no chip. */
.admin-agent-page .aa-dot {
    display: inline-block;
    width: 6px;
    height: 6px;
    border-radius: 999px;
    background: var(--aa-ink-muted);
}
.admin-agent-page .aa-dot-success {
    background: var(--aa-success);
}
.admin-agent-page .aa-dot-danger {
    background: var(--aa-danger);
}
.admin-agent-page .aa-dot-warning {
    background: var(--aa-warning);
}
.admin-agent-page .aa-dot-info {
    background: var(--aa-info);
}

/* Primary CTA — the only place accent yellow appears. */
.admin-agent-page .aa-cta {
    background: var(--aa-accent);
    color: var(--aa-accent-ink);
    border: 1px solid var(--aa-accent);
    border-radius: var(--aa-radius-sm);
    padding: 6px 12px;
    font-family: var(--aa-font-mono);
    font-weight: 500;
    font-size: 12.5px;
    line-height: 1.4;
    transition-property: background-color, border-color;
    transition-duration: 100ms;
    transition-timing-function: ease-out;
}
.admin-agent-page .aa-cta:hover {
    background: var(--aa-accent-hover);
    border-color: var(--aa-accent-hover);
}

/* Ghost button — mono, sharp, quiet. */
.admin-agent-page .aa-ghost {
    background: transparent;
    color: var(--aa-ink-soft);
    border: 1px solid var(--aa-border);
    border-radius: var(--aa-radius-sm);
    padding: 4px 8px;
    font-family: var(--aa-font-mono);
    font-size: 12.5px;
    line-height: 1.4;
    transition-property: background-color, border-color, color;
    transition-duration: 100ms;
    transition-timing-function: ease-out;
}
.admin-agent-page .aa-ghost:hover {
    background: var(--aa-surface-hover);
    border-color: var(--aa-border-strong);
}

/* Tactile press — buttons compress slightly (never below 0.96). */
.admin-agent-page button:active:not(:disabled) {
    scale: 0.98;
    transition-property: scale;
    transition-duration: 100ms;
    transition-timing-function: ease-out;
}

/* Focus ring — blue focus, not the yellow accent, so accent stays scarce. */
.admin-agent-page :focus-visible {
    outline: 2px solid var(--aa-focus);
    outline-offset: 1px;
    border-radius: var(--aa-radius-sm);
}

/*
 * ------------------------------------------------------------------
 * Transcript restyle — the chat surface is rendered by the shared
 * phoebe_chat_view component, which other pages also use, so its feel
 * is adjusted here with page-scoped selectors instead of editing the
 * shared component. Hooks are stable data-testids plus one structural
 * wrapper class each.
 * ------------------------------------------------------------------
 */

/* User message — compact mono card, sharp corners (Wiki session-user). */
.admin-agent-page .group\/msg > .flex.justify-end > div {
    border-radius: var(--aa-radius-sm);
    font-family: var(--aa-font-mono);
    font-size: 12.5px;
    line-height: 1.55;
}

/*
 * Tool execution card — a bounded console row: hairline frame and solid
 * fill so each tool call reads as a crisp unit inside the activity box
 * instead of free-floating mono text. Mono head text; purple accent on
 * hover.
 */
.admin-agent-page [data-testid='tool-execution-card'] {
    border-radius: var(--aa-radius-sm) !important;
    border-color: var(--aa-border);
    box-shadow: none !important;
    background: var(--aa-canvas);
    transition-property: border-color;
    transition-duration: 100ms;
    transition-timing-function: ease-out;
}
.admin-agent-page [data-testid='tool-execution-card']:hover {
    border-color: var(--aa-purple-border);
}
.admin-agent-page [data-testid='tool-execution-card'] > button {
    font-family: var(--aa-font-mono);
    font-size: 12px;
    padding-top: 6px;
    padding-bottom: 6px;
    transition-property: background-color, color;
    transition-duration: 100ms;
    transition-timing-function: ease-out;
}
.admin-agent-page [data-testid='tool-execution-card'] > button:hover {
    background: var(--aa-surface-sunk);
}

/*
 * Assistant message — a clearly boxed "agent said" block so agent
 * output reads instantly apart from the lighter-weight tool rows
 * ("agent did"). Purple-framed card: the brand color marks agent voice.
 */
.admin-agent-page [data-testid='assistant-message-content'] {
    border: 1px solid var(--aa-purple-border);
    border-left: 2px solid var(--aa-purple);
    border-radius: var(--aa-radius-sm);
    background: var(--aa-canvas);
    box-shadow: none !important;
    padding: 10px 14px;
}

/*
 * Collapsed activity group — the Wiki-style "2 thinking, 2 tool calls"
 * summary row. Mono chrome, purple hover; when expanded, the summary
 * row and dropdown share one bounding box (drawn by the component) with
 * a hairline divider between them.
 */
.admin-agent-page [data-testid='agent-activity-group-toggle'] {
    font-family: var(--aa-font-mono);
    font-size: 11.5px;
    color: var(--aa-ink-muted);
    border-radius: var(--aa-radius-sm);
    transition-property: background-color, color;
    transition-duration: 100ms;
    transition-timing-function: ease-out;
}
.admin-agent-page [data-testid='agent-activity-group-toggle']:hover {
    background: var(--aa-purple-wash);
    color: var(--aa-purple-strong);
}
.admin-agent-page [data-testid='agent-activity-group-body'] {
    border-top-color: var(--aa-border);
}

/*
 * Inline "Open full output" links inside tool cards come from the
 * shared chat view with an emerald tint; on this page links carry the
 * brand purple instead.
 */
.admin-agent-page [data-testid='tool-execution-card'] .text-emerald-700 {
    color: var(--aa-purple);
}
.admin-agent-page [data-testid='tool-execution-card'] .text-emerald-700:hover {
    color: var(--aa-purple-strong);
}

/* Code blocks inside the transcript — sharp, sunk, mono. */
.admin-agent-page [data-testid='assistant-message-content'] pre,
.admin-agent-page [data-testid='tool-execution-card'] pre {
    border-radius: var(--aa-radius-sm);
    background: var(--aa-surface-sunk);
}
